import React, { useCallback, useEffect, useState } from 'react';

import { Modal as AntdModal } from 'antd';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import translations from 'translations';
import utilsMessages from 'utils/messages';
import IcNoteIcon from '../icons/icNoteIcon';

const StyledModal = styled(AntdModal)`
  .ant-modal-content {
    padding: 0 !important;
    border-radius: 16px !important;
    overflow: hidden;
    box-shadow: 0 4px 24px rgba(0, 0, 0, 0.12) !important;
  }
  .ant-modal-body {
    padding: 0 !important;
  }
`;

const Container = styled.div`
  width: 100%;
  background: #ffffff;
  border-radius: 16px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const ContentSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 4px;
`;

const HeaderTitle = styled.h2`
  flex: 1;
  text-align: center;
  font-size: 20px;
  font-weight: 600;
  line-height: 28px;
  color: #111827;
  margin: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const CloseButton = styled.button`
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #4b5563;

  &:hover {
    opacity: 0.7;
  }
`;

const CloseIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M1 1L13 13M13 1L1 13" stroke="#4B5563" strokeWidth="1.7" strokeLinecap="round" />
  </svg>
);

const ProductNameRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ProductNameText = styled.span`
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
  color: #111827;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const InputSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`;

const FieldContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const LabelRow = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
`;

const Label = styled.span`
  font-size: 14px;
  font-weight: 500;
  line-height: 22px;
  color: #4b5563;
`;

const RequiredMark = styled.span`
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  color: #e2483c;
`;

const Textarea = styled.textarea`
  width: 100%;
  height: 100px;
  padding: 8px 16px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #ffffff;
  font-size: 16px;
  line-height: 24px;
  color: #111827;
  resize: none;
  outline: none;
  font-family: inherit;

  &:focus {
    border-color: #43a047;
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const Footer = styled.div`
  display: flex;
  gap: 12px;
`;

const SaveButton = styled.button<{ disabled?: boolean }>`
  width: 100%;
  height: 44px;
  border-radius: 8px;
  border: none;
  padding: 10px 16px;
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  background: ${({ disabled }) => (disabled ? '#f2f3f5' : '#43a047')};
  color: ${({ disabled }) => (disabled ? '#9ca3af' : '#ffffff')};
  transition: background 0.15s ease;
  flex: 1;

  &:not(:disabled):hover {
    background: #388e3c;
  }
`;

const DeleteButton = styled.button`
  width: 100%;
  height: 44px;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  padding: 10px 16px;
  font-size: 16px;
  font-weight: 800;
  line-height: 24px;
  cursor: pointer;
  background: #ffffff;
  color: #e2483c;
  transition: background 0.15s ease;
  flex: 1;

  &:hover {
    background: #fff5f5;
  }
`;

const ProductNoteModal = ({
  isOpen,
  onSubmit,
  onClose,
  productName,
  note,
  children,
  confirmLoading,
  disableOkButton,
}: {
  isOpen: boolean;
  disableOkButton?: boolean;
  onSubmit: (data) => void;
  onClose: () => void;
  onClickDeleteNote?: () => void;
  productName?: string;
  note: string;
  confirmLoading?: boolean;
  children?: JSX.Element | JSX.Element[] | '';
}) => {
  const [noteValue, onChangeNote] = useState(note);
  const [isHavePrevNote, setIsHavePrevNote] = useState(false);

  useEffect(() => {
    onChangeNote(note);
  }, [note]);

  useEffect(() => {
    if (note && !!note?.trim()) {
      setIsHavePrevNote(true);
    } else {
      setIsHavePrevNote(false);
    }
  }, [note]);

  const deleteNote = useCallback(() => {
    AntdModal.confirm({
      autoFocusButton: null,
      okButtonProps: { danger: true },
      title: translations(utilsMessages.titleRemoveNote),
      content: <p style={{ fontWeight: 300, marginTop: 16 }}>*{translations(utilsMessages.noteRemoveNote)}</p>,
      okText: translations(utilsMessages.yes),
      cancelText: translations(utilsMessages.no),
      onOk: () => {
        onSubmit('');
        onChangeNote('');
      },
    });
  }, [onSubmit, onChangeNote]);

  const handleSave = () => {
    onSubmit(noteValue.trim());
    onChangeNote('');
  };

  const isSaveDisabled = disableOkButton || (!isHavePrevNote && !noteValue.trim());

  return (
    <StyledModal open={isOpen} onCancel={onClose} centered closable={false} footer={null} destroyOnClose width={480}>
      <Container>
        <ContentSection>
          <HeaderRow>
            <div style={{ width: 24 }} />
            <HeaderTitle>{translations(utilsMessages.addNote)}</HeaderTitle>
            <CloseButton onClick={onClose}>
              <CloseIcon />
            </CloseButton>
          </HeaderRow>

          <InputSection>
            {productName && (
              <ProductNameRow>
                <IcNoteIcon />
                <ProductNameText>{productName}</ProductNameText>
              </ProductNameRow>
            )}

            <FieldContainer>
              <LabelRow>
                <Label>
                  <FormattedMessage {...utilsMessages.note} />
                </Label>
                <RequiredMark>*</RequiredMark>
              </LabelRow>
              <Textarea name="note" value={noteValue} onChange={(e) => onChangeNote(e.target.value)} autoFocus />
              {children}
            </FieldContainer>
          </InputSection>
        </ContentSection>

        <Footer>
          {isHavePrevNote && (
            <DeleteButton type="button" onClick={deleteNote}>
              <FormattedMessage {...utilsMessages.removeNote} />
            </DeleteButton>
          )}
          <SaveButton type="button" disabled={isSaveDisabled} onClick={!isSaveDisabled ? handleSave : undefined}>
            {confirmLoading ? '...' : <FormattedMessage {...utilsMessages.save} />}
          </SaveButton>
        </Footer>
      </Container>
    </StyledModal>
  );
};

export default ProductNoteModal;
